<script setup lang="ts">
import { useNotify } from '@/components/composition/notification'
import AppState, {
  contactLabelPluralLower,
  contactLabelSingular,
  getNestedCustomValues,
} from '@/states/app'
import { EmailNotifications } from '@/types/notification'
import { UIFormItem, UIInput, UISwitch } from '@gohighlevel/ghl-ui'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Calendar, { CalendarType } from '../../models/calendar'
import {
  additionalNotesFieldOptions,
  additionalNotesFieldOptionsV3,
  handleNestedCustomValueModification,
} from '../../utils/customValueMapper'
import { getTooltip } from '../../utils/generalFunctions'
import HeaderTile from '../common/HeaderTile.vue'
import Tooltip from '../common/Tooltip.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'
import CalendarNotificationList from './CalendarNotificationList.vue'
import DurationPicker from './DurationPicker.vue'

const defaultNotification = {
  type: 'email',
  shouldSendToContact: false,
  shouldSendToGuest: false,
  shouldSendToUser: false,
  shouldSendToSelectedUsers: false,
  templateId: 'default',
  selectedUsers: '',
}

const { t } = useI18n()
const { sendNotification } = useNotify()

const state = reactive({
  isLoading: false,
  notificationOptions: [
    {
      value: 'email',
      label: t('calendar_advanced.notifications.ack_email'),
    },
  ],
  formData: {
    notifications: [
      {
        ...defaultNotification,
      },
    ],
    notificationStatus: [] as EmailNotifications,
    googleInvitationEmails: false,
    shouldAssignContactToTeamMember: false,
    shouldSkipAssigningContactForExisting: false,
    allowReschedule: true,
    allowCancellation: true,
    notes: '' as any,
    reschedulePreference: {
      expiryTime: '',
      expiryTimeUnit: 'mins',
    },
    cancellationPreference: {
      expiryTime: '',
      expiryTimeUnit: 'mins',
    },
  },
})
const editingCalendar = computed(() => {
  return AppState.editingCalendar
})
defineEmits(['onSave', 'onCancel'])

const methods = {
  async loadData() {
    state.formData.googleInvitationEmails = Boolean(
      editingCalendar.value?.googleInvitationEmails
    )

    state.formData.shouldAssignContactToTeamMember = Boolean(
      editingCalendar.value?.shouldAssignContactToTeamMember
    )

    state.formData.shouldSkipAssigningContactForExisting = Boolean(
      editingCalendar.value?.shouldSkipAssigningContactForExisting
    )

    state.formData.allowReschedule = Boolean(
      editingCalendar.value?.allowReschedule
    )
    state.formData.allowCancellation = Boolean(
      editingCalendar.value?.allowCancellation
    )

    state.formData.notes = editingCalendar.value?.notes
    if (editingCalendar.value?.reschedulePreference) {
      state.formData.reschedulePreference = {
        expiryTimeUnit: String(
          editingCalendar.value?.reschedulePreference?.expiryTimeUnit
        ),
        expiryTime: String(
          editingCalendar.value?.reschedulePreference?.expiryTime ?? ''
        ),
      }
    }
    if (editingCalendar.value?.cancellationPreference) {
      state.formData.cancellationPreference = {
        expiryTimeUnit: String(
          editingCalendar.value?.cancellationPreference?.expiryTimeUnit
        ),
        expiryTime: String(
          editingCalendar.value?.cancellationPreference?.expiryTime ?? ''
        ),
      }
    }
    state.formData.notificationStatus =
      editingCalendar.value?.notificationStatus || []
  },
  emitSave() {
    const data = {
      googleInvitationEmails: state.formData.googleInvitationEmails,
      shouldAssignContactToTeamMember:
        state.formData.shouldAssignContactToTeamMember,
      shouldSkipAssigningContactForExisting:
        state.formData.shouldSkipAssigningContactForExisting,
      allowReschedule: state.formData.allowReschedule,
      allowCancellation: state.formData.allowCancellation,
      notes: state.formData.notes,
      reschedulePreference: {
        expiryTimeUnit: state.formData.reschedulePreference.expiryTimeUnit,
        expiryTime: parseInt(state.formData.reschedulePreference.expiryTime),
      },
      cancellationPreference: {
        expiryTimeUnit: state.formData.cancellationPreference.expiryTimeUnit,
        expiryTime: parseInt(state.formData.cancellationPreference.expiryTime),
      },
    }

    return data
  },
}

function toggleReschedule(value: boolean) {
  state.formData.allowReschedule = value
  if (state.formData.allowReschedule) {
    state.formData.notes = state.formData.notes.replace(
      Calendar.NotesText_Title_RescheduleOrCancellation,
      ''
    )
    if (state.formData.notes.includes(Calendar.NotesText_PhoneEmail)) {
      state.formData.notes = state.formData.notes.replace(
        Calendar.NotesText_PhoneEmail,
        ''
      )
      state.formData.notes =
        `${Calendar.NotesText_PhoneEmail}\n${Calendar.NotesText_Title_RescheduleOrCancellation}\n${Calendar.NotesText_Reschedule}` +
        state.formData.notes
    } else if (state.formData.notes.includes(Calendar.NotesText_Cancellation)) {
      state.formData.notes = state.formData.notes.replace(
        Calendar.NotesText_Cancellation,
        ''
      )
      state.formData.notes =
        state.formData.notes +
        `\n${Calendar.NotesText_Title_RescheduleOrCancellation}\n${Calendar.NotesText_Reschedule}\n\n` +
        Calendar.NotesText_Cancellation
    } else {
      state.formData.notes =
        state.formData.notes +
        `\n\n${Calendar.NotesText_Title_RescheduleOrCancellation}\n${Calendar.NotesText_Reschedule}\n`
    }
  } else {
    state.formData.notes = state.formData.notes.replace(
      Calendar.NotesText_Reschedule,
      ''
    )

    if (!state.formData.notes.includes(Calendar.NotesText_Cancellation)) {
      state.formData.notes = state.formData.notes.replace(
        Calendar.NotesText_Title_RescheduleOrCancellation,
        ''
      )
    }
  }
  removeAdditionalSpaces()
}

function toggleCancellation(value: boolean) {
  state.formData.allowCancellation = value

  if (state.formData.allowCancellation) {
    if (
      !state.formData.notes.includes(
        Calendar.NotesText_Title_RescheduleOrCancellation
      )
    ) {
      state.formData.notes =
        state.formData.notes +
        `\n${Calendar.NotesText_Title_RescheduleOrCancellation}\n${Calendar.NotesText_Cancellation}`
    } else {
      state.formData.notes =
        state.formData.notes + `\n${Calendar.NotesText_Cancellation}`
    }
  } else {
    state.formData.notes = state.formData.notes.replace(
      Calendar.NotesText_Cancellation,
      ''
    )
    state.formData.notes = state.formData.notes.replace(
      Calendar.NotesText_Title_RescheduleOrCancellation,
      ''
    )
  }
  removeAdditionalSpaces()
}

const removeAdditionalSpaces = () => {
  state.formData.notes = state.formData.notes.replace(/\n{3,}/g, '\n')
}

const toggleNotification = async (value: any) => {
  const item = state.formData.notificationStatus.find(
    el => el.type === value.type
  )
  const channel = item?.channels.find(ch => ch.name === value.channel)
  if (channel) {
    channel.isActive = value.status
  }
}

defineExpose({
  methods,
})

const additionalNotesRef = ref(additionalNotesFieldOptionsV3)

// Function to load and update custom values with nested folder structure
const updateAdditionalNotesRef = async () => {
  const additionalNotesOptions = editingCalendar.value?.isEventCalendar
    ? additionalNotesFieldOptions
    : additionalNotesFieldOptionsV3

  // Set base options first (synchronous)
  additionalNotesRef.value = additionalNotesOptions

  // Then fetch and add custom values (asynchronous)
  const nestedCustomValues = await getNestedCustomValues()
  const customValues = handleNestedCustomValueModification(nestedCustomValues)

  if (customValues) {
    additionalNotesRef.value = [...additionalNotesOptions, customValues]
  }
}

watch(
  () => editingCalendar.value?.id,
  newId => {
    if (newId) {
      updateAdditionalNotesRef()
    }
  },
  { immediate: true }
)

watch(
  () => editingCalendar.value,
  () => {
    methods.loadData()
  },
  { immediate: true }
)

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const insertAdditionalNotes = (value: string) => {
  state.formData.notes = state.formData.notes + ' ' + value
}

const showAssignUserToContact = computed(() => {
  const actualType = editingCalendar.value?.calendarType
  const result = actualType === CalendarType.ROUND_ROBIN ||
        actualType === CalendarType.COLLECTIVE ||
        actualType === CalendarType.CLASS_BOOKING ||
        actualType === CalendarType.SERVICE ||
        actualType === CalendarType.PERSONAL
  return result
})
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="text-only-header"
      :header="$t('calendar_advanced.notifications.notifications')"
      :description="$t('calendar_advanced.notifications.notifications_sub')"
    />
    <div class="my-[15px] border-t border-gray-200" />

    <CalendarNotificationList
      id="calendar-email-notification-list"
      :calendar-id="editingCalendar?.id"
      :notifications="state.formData.notificationStatus"
      @toggle-notification="toggleNotification"
    />
  </div>

  <div class="mt-[16px] rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="text-only-header"
      :header="$t('calendar_advanced.notifications.additional_settings')"
      :description="
        $t('calendar_advanced.notifications.additional_settings_desc')
      "
    />

    <div class="border-b border-gray-200 pb-[16px]" />

    <div
      class="mt-[8px] flex flex-col gap-4 border-b border-gray-200 py-[16px]"
      v-if="showAssignUserToContact"
    >
      <div class="flex flex-row gap-3">
        <UISwitch
          id="calendar-assign-user-to-contact"
          v-model:value="state.formData.shouldAssignContactToTeamMember"
          size="small"
        />
        <div class="mt-[-3px] flex flex-col">
          <div class="text-[14px] font-medium leading-6 text-gray-600">
            {{
              $t('calendar_advanced.notifications.assign_user_to_contact', {
                contactsLabel: contactLabelPluralLower,
              })
            }}
          </div>
          <div class="text-[13px] font-normal leading-6 text-gray-500">
            {{
              $t(
                'calendar_advanced.notifications.assign_user_to_contact_desc',
                { contactLabel: contactLabelSingular }
              )
            }}
          </div>
        </div>
      </div>

      <div
        class="flex flex-row gap-3"
        v-if="state.formData.shouldAssignContactToTeamMember"
      >
        <UISwitch
          id="skip-assigning-user-to-contact"
          v-model:value="state.formData.shouldSkipAssigningContactForExisting"
          size="small"
        />
        <div class="mt-[-3px] flex flex-col">
          <div class="text-[14px] font-medium leading-6 text-gray-600">
            {{
              $t(
                'calendar_advanced.notifications.skip_assigning_user_to_contact',
                { contactLabel: contactLabelSingular }
              )
            }}
          </div>
          <div class="text-[13px] font-normal leading-6 text-gray-500">
            {{
              $t(
                'calendar_advanced.notifications.skip_assigning_user_to_contact_desc',
                { contactLabel: contactLabelSingular }
              )
            }}
          </div>
        </div>
      </div>
    </div>
    <UIFormItem
      :label="
        $t('calendar_advanced.notifications.cancellation_and_reschedule_policy')
      "
      class="w-full pt-[20px]"
      :show-feedback="false"
    >
      <div id="calendar-reschedule-cancel" class="flex flex-col">
        <div class="flex flex-col items-start pb-1 pt-4">
          <div class="flex flex-row gap-3">
            <div id="allow-reschedule">
              <UISwitch
                v-model:value="state.formData.allowReschedule"
                :aria-label="$t('aria_labels.allow_reschedule')"
                @update:value="value => toggleReschedule(value)"
                size="small"
              />
            </div>
            <div class="font-normal">
              {{
                isServiceCalendar
                  ? $t('calendar_advanced.extras.allow_reschedule_appointment')
                  : $t('calendar_advanced.notifications.allow_reschedule')
              }}
            </div>
            <Tooltip
              :message="
                $t('calendar_advanced.notifications.allow_reschedule_tooltip')
              "
            />
          </div>

          <div
            v-if="state.formData.allowReschedule"
            class="flex w-full flex-row items-center gap-3"
          >
            <div class="font-normal text-gray-600">
              {{ $t('calendar_advanced.notifications.reschedule_link_expiry') }}
            </div>
            <DurationPicker
              :apply-margin-top="false"
              class="w-[30%]"
              path="reschedulePreference"
              :value="state.formData.reschedulePreference.expiryTime"
              :unit="state.formData.reschedulePreference.expiryTimeUnit"
              @on-value-change="
                value => {
                  state.formData.reschedulePreference.expiryTime =
                    value.duration
                  state.formData.reschedulePreference.expiryTimeUnit =
                    value.timeUnit
                }
              "
            />
            <div class="font-normal text-gray-600">
              {{ $t('calendar_advanced.notifications.before_meeting') }}
            </div>
          </div>
        </div>
        <div class="flex flex-col items-start pb-1 pt-4">
          <div class="flex flex-row gap-3">
            <div id="allow-cancellation">
              <UISwitch
                v-model:value="state.formData.allowCancellation"
                @update:value="value => toggleCancellation(value)"
                size="small"
              />
            </div>
            <div class="font-normal">
              {{
                isServiceCalendar
                  ? $t('calendar_advanced.extras.allow_cancelling_appointment')
                  : $t('calendar_advanced.notifications.allow_cancellation')
              }}
            </div>
            <Tooltip
              :message="
                $t('calendar_advanced.notifications.allow_cancellation_tooltip')
              "
            />
          </div>

          <div
            v-if="state.formData.allowCancellation"
            class="flex w-full flex-row items-center gap-3"
          >
            <div class="font-normal text-gray-600">
              {{
                $t('calendar_advanced.notifications.cancellation_link_expiry')
              }}
            </div>
            <DurationPicker
              :apply-margin-top="false"
              class="w-[30%]"
              path="reschedulePreference"
              :value="state.formData.cancellationPreference.expiryTime"
              :unit="state.formData.cancellationPreference.expiryTimeUnit"
              @on-value-change="
                value => {
                  state.formData.cancellationPreference.expiryTime =
                    value.duration
                  state.formData.cancellationPreference.expiryTimeUnit =
                    value.timeUnit
                }
              "
            />
            <div class="font-normal text-gray-600">
              {{ $t('calendar_advanced.notifications.before_meeting') }}
            </div>
          </div>
        </div>
      </div>
    </UIFormItem>
  </div>

  <div class="mt-[16px] rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="text-only-header"
      :header="$t('calendar_advanced.notifications.third_party_cal')"
      :description="$t('calendar_advanced.notifications.third_party_cal_desc')"
    />
    <div class="my-[15px] border-t border-gray-200 pb-[8px]" />
    <div class="flex flex-row gap-3">
      <UISwitch
        id="calendar-notifications-send-google-invites"
        v-model:value="state.formData.googleInvitationEmails"
        size="small"
      />
      <div class="mt-[-3px] text-[14px] font-medium leading-6 text-gray-600">
        {{
          editingCalendar?.calendarType === CalendarType.EVENT
            ? $t('calendar_advanced.notifications.send_google_invites')
            : $t('calendar_advanced.notifications.send_google_outlook_invites')
        }}
      </div>
    </div>
    <UIFormItem
      :label="$t('calendar_advanced.notifications.meeting_invite_notes')"
      class="mt-[20px] w-full"
      :show-feedback="false"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.notifications.meeting_invite_notes_tooltip')
        )
      "
    >
      <UIInput
        id="calendar-additional-notes"
        v-model="state.formData.notes"
        :rows="5"
        type="textarea"
        :placeholder="$t('placeholders.notes')"
        class="relative"
      >
        <template #suffix>
          <CustomVariablePickerV2
            :dropdown-options="additionalNotesRef"
            @update:selected-value="val => insertAdditionalNotes(val)"
            :button-class="'absolute bottom-0 right-4 cursor-pointer'"
          />
        </template>
      </UIInput>
    </UIFormItem>
  </div>
</template>

<style scoped lang="scss">
#calendar-reschedule-cancel {
  .n-divider:not(.n-divider--vertical) {
    margin: 0px 0px 10px 0px;
  }

  .n-form-item-feedback-wrapper {
    display: none;
  }
}
</style>
